import * as Sentry from "@sentry/react";
import type { Identity } from "core";
import posthog from "posthog-js";
import React from "react";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { isDeployed, sentryDsn } from "./config.ts";

export function setSentryUser(identity: Pick<Identity, "email" | "id">) {
  Sentry.setUser(identity);
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function captureAndReportError(error: Error) {
  console.error(error);
  Sentry.captureException(error);
}

export function initSentry() {
  if (Sentry.isInitialized()) {
    console.warn("Sentry already initialized");
    return;
  }

  Sentry.init({
    dsn: sentryDsn,

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      /^https:\/\/beta.getnoan\.com/,
      /^https:\/\/app.getnoan\.com/,
    ],

    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      posthog.sentryIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    enabled: isDeployed,
    /**
     * v11 defaults to collecting cookies, bodies and GenAI content. Keep the v10 restrictive defaults.
     */
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: true,
      genAI: { inputs: false, outputs: false },
    },
  });
}

export const createSentryBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);
